// @flow

import React, { useState, useEffect } from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import { push } from "connected-react-router";
import { route } from "@routes/helper";

import * as serviceSelectors from "@modules/service/selectors";
import actionCreators from "@modules/service/actionCreator";
import type { State } from "@store/reducers";
import type { RTWProcessesData, RtwProcessType } from "@modules/service/reducer/rtw/types";
import { Button, ButtonGroup, GSGrid, Pagination, View, Loader, RtwAddModal } from "@components";
import { formatDate } from "@transformers/date";
import { formatAuthoredBy } from "@transformers/names";
import classNames from "classnames";
import sharedActionCreators from "@modules/shared/actionCreator";
import type { HierarchyAssignment } from "@modules/service/types";

type FilterState = null | boolean;

type OwnProps = $Exact<{
  clientId: number,
}>;

type PropsFromState = $Exact<{
  rtwData: RTWProcessesData,
  isLoading: boolean,
  schema: Object | null,
  assignments: Array<HierarchyAssignment>,
}>;

type PropsFromDispatch = $Exact<{
  push: typeof push,
  getRtwData: typeof actionCreators.GetRtwProcesses,
  newRtw: typeof actionCreators.NewRtw,
  getSchema: typeof actionCreators.GetRtwSchema,
  getHierarchyTree: typeof sharedActionCreators.GetHierarchyTree,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

type StateProps = $Exact<{
  filter: FilterState,
}>;

function RtwTable(props: Props) {
  const { clientId } = props;
  const [filter, setFilter] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    getRtwData();
  }, [clientId, filter]);

  useEffect(() => {
    props.getSchema({ clientId });
    props.getHierarchyTree({ clientId });
  }, []);

  const toggleModal = () => {
    setIsModalOpen(prev => !prev);
  };

  const data = props.rtwData.content;
  const columns = [
    {
      name: props.t("ID"),
      align: "center",
      width: 60,
      key: "id",
    },
    {
      name: props.t("RETURN_TO_WORK_PROCESS_NAME"),
      renderCell: ({ row }: { row: RtwProcessType }) => GSGrid.render.ellipsis(row.content.name),
      align: "left",
    },

    {
      name: props.t("DATE_CREATED"),
      width: 100,
      typeConfig: "date",
      renderCell: ({ row }: { row: RtwProcessType }) => formatDate(row.createdAt),
    },
    {
      name: props.t("CREATED_BY"),
      width: "min-content",
      renderCell: ({ row }: { row: RtwProcessType }) => formatAuthoredBy(row.createdBy),
      align: "left",
    },

    {
      key: "questions",
      name: props.t("QUESTIONS"),
      tooltip: {
        title: props.t("BEST_PRACTICE"),
        text: props.t("SERVICE_FEATURES_RTW_TABLE_QUESTIONS"),
      },
      renderCell: ({ row }: { row: RtwProcessType }) => {
        const questions = getColumnValueAndScore(row, "questions");
        return getColorPillRender(questions.value, questions.isScoring);
      },
    },

    {
      key: "attachments",
      name: props.t("Attachments"),
      tooltip: {
        title: props.t("BEST_PRACTICE"),
        text: props.t("SERVICE_FEATURES_RTW_TABLE_ATTACHMENTS"),
      },
      renderCell: ({ row }: { row: RtwProcessType }) => {
        const attachments = getColumnValueAndScore(row, "attachments");
        return getColorPillRender(attachments.value, attachments.isScoring);
      },
    },

    {
      key: "kpiTarget",
      name: props.t("KPI_TARGET"),
      tooltip: {
        title: props.t("BEST_PRACTICE"),
        text: props.t("SERVICE_FEATURES_RTW_TABLE_KPI_TARGET"),
      },
      renderCell: ({ row }: { row: RtwProcessType }) => {
        const kpiMeasure = getColumnValueAndScore(row, "kpiMeasure");
        return getColorPillRender(kpiMeasure.value, kpiMeasure.isScoring);
      },
    },

    {
      key: "expiry",
      name: props.t("EXPIRY"),
      tooltip: {
        title: props.t("BEST_PRACTICE"),
        text: props.t("SERVICE_FEATURES_RTW_TABLE_EXPIRY"),
      },
      renderCell: ({ row }: { row: RtwProcessType }) => {
        const expiryPeriodInSeconds = getColumnValueAndScore(row, "expiryPeriodInSeconds");
        return getColorPillRender(expiryPeriodInSeconds.value, expiryPeriodInSeconds.isScoring);
      },
    },

    {
      key: "lateReasonCapture",
      name: props.t("LATE_CAPTURE"),
      tooltip: {
        title: props.t("BEST_PRACTICE"),
        text: props.t("SERVICE_FEATURES_RTW_TABLE_LATE_CAPTURE"),
      },
      renderCell: ({ row }: { row: RtwProcessType }) => {
        const lateReasonCapture = getColumnValueAndScore(row, "lateReasonCapture");
        return getColorPillRender(lateReasonCapture.value, lateReasonCapture.isScoring);
      },
    },
    {
      key: "rating",
      name: props.t("RATING"),
      renderCell: ({ row }: { row: RtwProcessType }) => {
        const valuesAndScore = Object.values(getAllValuesAndScores(row));

        if (!Array.isArray(valuesAndScore)) return;
        //$FlowIgnore
        const totalScore = valuesAndScore.filter(v => v.isScoring === true).length;
        return GSGrid.render.scorePill(totalScore);
      },
    },
    {
      name: props.t("STATUS"),
      width: 105,
      renderCell: ({ row }: { row: RtwProcessType }) =>
        GSGrid.render.statusPill(props.t(row.content.isActive ? "ACTIVE" : "INACTIVE")),
    },
    {
      name: props.t("VIEW"),

      typeConfig: "action",
      onCellClick: (row: RtwProcessType) => handleViewRevision(row.id),
      renderCell: ({ row }: { row: RtwProcessType }) => GSGrid.render.materialIcon("search"),
    },
  ];

  type ValueAndScore = {
    value: any,
    isScoring: boolean,
  };

  type ValuesAndScoreRecord = {
    questions: ValueAndScore,
    attachments: ValueAndScore,
    kpiMeasure: ValueAndScore,
    expiryPeriodInSeconds: ValueAndScore,
    lateReasonCapture: ValueAndScore,
  };

  function getColumnValueAndScore(row: RtwProcessType, column: string): ValueAndScore {
    switch (column) {
      case "questions": {
        const questions = row.content.formQuestions.filter(q => q.isActive && q.type === "QUESTION")
          .length;
        return { value: questions, isScoring: questions >= 11 && questions <= 25 };
      }
      case "attachments": {
        const attachments = row.content.attachments.length;
        return { value: attachments, isScoring: attachments > 0 };
      }
      case "kpiMeasure": {
        const kpiMeasure = row.content.kpis.at(-1).kpiMeasure;
        return { value: kpiMeasure, isScoring: kpiMeasure < 49 };
      }
      case "expiryPeriodInSeconds": {
        const expiryPeriodInSeconds = Math.round(row.content.expiryPeriodInSeconds / 60 / 60 / 24);
        return { value: expiryPeriodInSeconds, isScoring: expiryPeriodInSeconds < 91 };
      }
      default: {
        const lateReasonCapture = !!row.content.lateReasonCapture;
        return { value: props.t(lateReasonCapture ? "YES" : "NO"), isScoring: lateReasonCapture };
      }
    }
  }

  function getAllValuesAndScores(row: RtwProcessType): ValuesAndScoreRecord {
    return {
      questions: getColumnValueAndScore(row, "questions"),
      attachments: getColumnValueAndScore(row, "attachments"),
      kpiMeasure: getColumnValueAndScore(row, "kpiMeasure"),
      expiryPeriodInSeconds: getColumnValueAndScore(row, "expiryPeriodInSeconds"),
      lateReasonCapture: getColumnValueAndScore(row, "lateReasonCapture"),
    };
  }

  function getColorPillRender(value: number | string | boolean, isGreen: boolean) {
    return GSGrid.render.colorPill(value, isGreen && "mint", isGreen && "mint-d2");
  }

  function handleViewRevision(id: number) {
    props.push(
      route("ADMINISTRATION_SERVICE_RETURN_TO_WORK_DETAILS", {
        clientId,
        processId: id,
      }),
    );
  }
  function getRtwData(page?: number) {
    props.getRtwData({
      clientId,
      page,
      isActive: filter,
    });
  }

  function handleChangeFilter(filter: FilterState) {
    return function() {
      setFilter(filter);
    };
  }

  function handleAddSetup() {
    props.newRtw();
    toggleModal();
  }

  return (
    <View>
      {props.isLoading && <Loader />}
      <View className="message-box" margin={[0, 0, "1rem", 0]}>
        {props.t("RTW_INFORMATION")}
      </View>
      <View className="gs-container">
        <View direction="row" justifyContent="space-between" margin={[0, 0, "1rem", 0]}>
          <ButtonGroup
            config={[
              {
                color: filter === null && "selected",
                label: props.t("ALL"),
                onClick: handleChangeFilter(null),
              },
              {
                color: filter === true && "selected",
                label: props.t("ACTIVE"),
                onClick: handleChangeFilter(true),
              },
              {
                color: filter === false && "selected",
                label: props.t("INACTIVE"),
                onClick: handleChangeFilter(false),
              },
            ]}
          ></ButtonGroup>
          <Button materialIcon="add_box" onClick={handleAddSetup}>
            {props.t("ADD")}
          </Button>
        </View>

        <View>
          {data.length ? (
            <View>
              <GSGrid columns={columns} rows={props.rtwData.content} />
              <Pagination
                className="my-3 d-flex justify-content-center"
                currentPage={props.rtwData.pagination.pageNumber}
                totalPages={Math.ceil(
                  props.rtwData.totalElements / props.rtwData.pagination.pageSize,
                )}
                onChange={getRtwData}
              />
            </View>
          ) : (
            <h4 className="text-pinkish-grey text-center mt-5">
              {props.t("RETURN_TO_WORK_LIVE_REASONS")}
            </h4>
          )}
        </View>
      </View>
      {isModalOpen && props.schema && (
        <RtwAddModal
          clientId={clientId}
          editing={isModalOpen}
          isActive={filter}
          onCancel={toggleModal}
          schema={props.schema}
        />
      )}
    </View>
  );
}

const mapStateToProps = (state: State): PropsFromState => ({
  rtwData: serviceSelectors.getRtwProcessesDataSelector(state),
  isLoading: serviceSelectors.isLoading(state),
  schema: serviceSelectors.getRtwSchema(state),
  assignments: serviceSelectors.getRtwAssignments(state),
});

const mapDispatchToProps: PropsFromDispatch = {
  push,
  getRtwData: actionCreators.GetRtwProcesses,
  newRtw: actionCreators.NewRtw,
  getSchema: actionCreators.GetRtwSchema,
  getHierarchyTree: sharedActionCreators.GetHierarchyTree,
};

export default compose(localize(), connect(mapStateToProps, mapDispatchToProps))(RtwTable);
